import { TMemberCare } from '@/types/MemberCare.types';
import React from 'react';
import DirectoryCardBase from './DirectoryCardBase';
import { getMemberCareRoleFromName } from '@/utils/stringUtils';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import Calendly from '@/components/molecules/Calendly';
import CustomButton from '@/components/atoms/CustomButton';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import SvgIconCalendar from '@/components/atoms/SvgIconCalendar';
import colors from '@/theme/colors';
import { mixpanelEvents } from '@/constants/mixpanel';
import SendEmailModalWithTrigger from '@/modules/user/SendEmailModalWithTrigger';
import { useNavigate } from '@tanstack/react-router';

type Props = {
  member: TMemberCare;
  showStar?: boolean;
};

const ACTION_BUTTON_WIDTH = '120px';
const BIO_TOGGLE_THRESHOLD = 125;
const COLLAPSED_BIO_LINES = 5;
const BIO_LINE_HEIGHT = 20;

const MemberCareDirectoryCard = ({ member, showStar = false }: Props) => {
  const { trackEvent } = useTelemetry();
  const navigate = useNavigate();
  const [isCalendlyOpen, setIsCalendlyOpen] = React.useState(false);

  const name = `${member.firstName} ${member.lastName}`;
  const memberBio = member.bio ?? 'No About Me';
  const normalizedBio = memberBio.trimEnd();
  const shouldShowReadMore =
    normalizedBio.length > BIO_TOGGLE_THRESHOLD || normalizedBio.split(/\r\n|\r|\n/).length > COLLAPSED_BIO_LINES;

  const role = getMemberCareRoleFromName(name);

  return (
    <DirectoryCardBase
      pictureUrl={member.profilePicture}
      pictureLink={'/directory/member-care/$memberCareId'}
      routeParams={{ memberCareId: member.user_id }}
      name={name}
      roleSubtitle={role}
      star={showStar ? { kind: 'member-care', id: member.user_id, starred: Boolean(member.starred) } : undefined}
    >
      <VStack flex={1} minHeight={0} align={'start'} gap={'4px'} width={'100%'}>
        <Text variant={'urbanistExtraBoldSmall'} color={'text.mediumGray'}>
          ABOUT ME
        </Text>
        <Box position={'relative'} width={'100%'}>
          <Text
            width={'100%'}
            whiteSpace={'pre-wrap'}
            variant={'urbanistSemiBoldRegular'}
            color={'text.mediumBlue'}
            lineHeight={`${BIO_LINE_HEIGHT}px`}
            noOfLines={COLLAPSED_BIO_LINES}
            // paddingRight={shouldShowReadMore ? '104px' : undefined}
          >
            {normalizedBio}
          </Text>
          {shouldShowReadMore && (
            <Text
              as={'button'}
              variant={'urbanistExtraBoldSmall'}
              color={'text.mediumGray'}
              cursor={'pointer'}
              position={'absolute'}
              left={0}
              bottom={-5}
              lineHeight={`${BIO_LINE_HEIGHT}px`}
              onClick={() =>
                navigate({
                  to: '/directory/member-care/$memberCareId',
                  params: { memberCareId: member.user_id },
                })
              }
            >
              READ MORE {'\u2192'}
            </Text>
          )}
        </Box>
      </VStack>
      <HStack width={'100%'} justifyContent={'space-between'} marginTop={'auto'}>
        {member.calendly_link ? (
          <VStack gap={0}>
            <Calendly isOpen={isCalendlyOpen} setIsOpen={setIsCalendlyOpen} url={member.calendly_link} />
            <CustomButton
              isTransparent
              label="Schedule"
              hoverBackgroundColor="primary.500"
              labelColor="primary.500"
              borderHoverColor="primary.500"
              onClick={() => {
                setIsCalendlyOpen(true);
                trackEvent(mixpanelEvents.MEMBER_CARE_CALL_SCHEDULED);
              }}
              width={ACTION_BUTTON_WIDTH}
              labelHoverColor="white"
              iconBeforeLabelJSX={<SvgIconCalendar color={colors.primary[500]} size={18} />}
              iconBeforeLabelHoveredJSX={<SvgIconCalendar color={colors.extra.white} size={18} />}
              disabled={member?.calendly_link === ''}
            />
          </VStack>
        ) : (
          <Text variant={'urbanistSemiBoldRegular'} color={'text.mediumBlue'} paddingX={'10px'}>
            No Calendly Link
          </Text>
        )}

        <SendEmailModalWithTrigger
          peerSupportOrMemberCare={'member-care'}
          recipientId={member.user_id}
          toEmailName={`${member.firstName} ${member.lastName}`}
          triggerButtonLabel="Email"
          triggerButtonWidth={ACTION_BUTTON_WIDTH}
          backgroundColor={colors.extra.white}
          noShadow
        />
      </HStack>
    </DirectoryCardBase>
  );
};

export default MemberCareDirectoryCard;
